import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1604705528621-81b2755a320b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxyYWNpbmclMjBjYXIlMjBuZW9ufGVufDB8fHx8MTc5MzU5MTQzM3ww&ixlib=rb-4.1.0&q=85";

const MARQUEE_ITEMS =
  "TURBOCHARGERS • ENGINE SWAPS • NITROUS KITS • DRIFT TUNING • SEMI-SLICKS • FULL BUILDS • COMMISSIONS PAID REAL-TIME • トップギア • ";

function RevBoot({ onDone }) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    let v = 0;
    const t = setInterval(() => {
      v += Math.random() * 14 + 6;
      if (v >= 100) {
        setPct(100);
        clearInterval(t);
        setTimeout(onDone, 450);
      } else setPct(Math.floor(v));
    }, 110);
    return () => clearInterval(t);
  }, [onDone]);

  const R = 86;
  const C = 2 * Math.PI * R;
  return (
    <motion.div
      className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center carbon-bg"
      exit={{ opacity: 0, scale: 1.04 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid="boot-sequence"
    >
      <div className="relative">
        <svg width="220" height="220" viewBox="0 0 220 220" className="-rotate-90">
          <circle cx="110" cy="110" r={R} stroke="#1B261F" strokeWidth="6" fill="none" />
          <circle
            cx="110"
            cy="110"
            r={R}
            stroke="#00FF66"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C - (C * pct) / 100}
            style={{ filter: "drop-shadow(0 0 8px rgba(0,255,102,0.6))" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-4xl font-black text-primary neon-glow" data-testid="boot-percent">
            {pct}
          </span>
          <span className="text-[10px] tracking-[0.4em] text-muted-foreground mt-1">RPM</span>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-center">
        <Logo size={230} />
      </div>
      <span className="font-jp text-[10px] text-muted-foreground mt-3 tracking-[0.3em]">
        起動中 — IGNITION
      </span>
    </motion.div>
  );
}

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Login() {
  const { user, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const [booted, setBooted] = useState(false);
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const submit = (ev) => {
    ev.preventDefault();
    setBusy(true);
    setError("");
    const attempt =
      mode === "signin" ? login(email, password) : register(name, email, password);
    attempt.then((res) => {
      setBusy(false);
      if (res.ok) navigate("/");
      else setError(res.error);
    });
  };

  return (
    <div ref={wrapRef} className="min-h-screen bg-background carbon-bg relative overflow-hidden" data-testid="login-page">
      <AnimatePresence>{!booted && <RevBoot onDone={() => setBooted(true)} />}</AnimatePresence>

      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute inset-0 grid-lines" />

      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: booted ? 1 : 0, x: booted ? 0 : -16 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center gap-3"
        >
          <Logo size={170} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: booted ? 1 : 0, x: booted ? 0 : 16 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex items-center gap-2 text-[10px] tracking-[0.3em] text-muted-foreground"
          data-testid="garage-status"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
          GARAGE ONLINE
        </motion.div>
      </header>

      <main className="relative z-10 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center px-6 lg:px-12 pt-8 lg:pt-16 pb-16 max-w-7xl mx-auto">
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: booted ? 1 : 0 }}
            transition={{ delay: 0.4 }}
            className="text-[10px] tracking-[0.45em] text-primary mb-6 flex items-center gap-3"
            data-testid="login-overline"
          >
            <span className="h-px w-10 bg-primary/60" />
            トップギア PERFORMANCE GARAGE
          </motion.div>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-foreground">
            {["WELCOME TO", "TOP GEAR"].map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className={`block text-4xl sm:text-5xl lg:text-6xl ${i === 1 ? "text-primary neon-glow" : ""}`}
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate={booted ? "show" : "hidden"}
                  data-testid={`hero-line-${i}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: booted ? 1 : 0, y: booted ? 0 : 14 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 text-sm text-muted-foreground max-w-md leading-relaxed"
          >
            Clock in. Wrench on. Cash out.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: booted ? 1 : 0, y: booted ? 0 : 24 }}
            transition={{ duration: 0.8, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-md"
            data-testid="rx7-hero-frame"
          >
            <div
              className="relative border border-primary/40 overflow-hidden shadow-[0_0_40px_rgba(0,255,102,0.12)] group"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 26px), calc(100% - 26px) 100%, 0 100%)" }}
            >
              <img
                src="/rx7-studio.jpg"
                alt="Green widebody Mazda RX-7 FD"
                className="w-full h-52 sm:h-64 object-cover object-[center_75%] transition-transform duration-700 group-hover:scale-105"
                data-testid="rx7-hero-img"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-2.5 left-3 flex items-center gap-2 text-[9px] tracking-[0.3em] text-primary bg-black/70 border border-primary/30 px-2.5 py-1.5">
                SHOP DREAM BUILD — FD RX-7 <span className="font-jp text-primary/60 tracking-normal">ロータリー魂</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: booted ? 1 : 0, y: booted ? 0 : 24 }}
          transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="justify-self-end w-full max-w-md"
        >
          <div className="backdrop-blur-xl bg-black/70 border border-primary/20 shadow-[0_8px_40px_rgba(0,0,0,0.6)] rounded-md p-7" data-testid="login-terminal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2 text-primary">
                <Zap size={14} />
                <span className="font-display text-[11px] tracking-[0.3em]">CREW TERMINAL</span>
              </div>
              <span className="font-jp text-[10px] text-muted-foreground">従業員専用</span>
            </div>
            <div className="grid grid-cols-2 gap-1 mb-5 bg-secondary/50 border border-border rounded-sm p-1" data-testid="auth-mode-tabs">
              <button
                type="button"
                data-testid="signin-tab"
                onClick={() => { setMode("signin"); setError(""); }}
                className={`text-[10px] font-display tracking-[0.2em] py-2 rounded-sm transition-colors ${mode === "signin" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`}
              >
                SIGN IN
              </button>
              <button
                type="button"
                data-testid="register-tab"
                onClick={() => { setMode("register"); setError(""); }}
                className={`text-[10px] font-display tracking-[0.2em] py-2 rounded-sm transition-colors ${mode === "register" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`}
              >
                JOIN CREW
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              {mode === "register" && (
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">YOUR NAME</label>
                  <Input
                    data-testid="register-name-input"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Maria Ceasar"
                    className="bg-secondary/60 border-input focus-visible:ring-primary h-11 rounded-sm text-sm"
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">EMAIL</label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="crew@topgear.com"
                  className="bg-secondary/60 border-input focus-visible:ring-primary h-11 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">PASSWORD</label>
                <Input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-secondary/60 border-input focus-visible:ring-primary h-11 rounded-sm text-sm"
                />
              </div>
              {error && (
                <div className="text-destructive text-xs border border-destructive/40 bg-destructive/10 px-3 py-2 rounded-sm" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={busy}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-xs tracking-[0.25em] h-12 rounded-sm"
              >
                {busy
                  ? "STARTING ENGINE…"
                  : mode === "signin"
                    ? "IGNITION →"
                    : "JOIN THE CREW →"}
              </Button>
            </form>
            <p className="mt-5 text-[10px] text-muted-foreground text-center tracking-wide">
              {mode === "signin"
                ? "Built by Jackie"
                : "New mechanics start at 25% commission — the boss can adjust your rate, or suspend access, anytime."}
            </p>
          </div>
        </motion.div>
      </main>

      <div className="absolute bottom-0 left-0 right-0 border-t border-primary/15 bg-black/60 backdrop-blur-md overflow-hidden py-3 z-10" data-testid="editorial-marquee">
        <div className="animate-marquee whitespace-nowrap flex">
          {[0, 1].map((k) => (
            <span key={k} className="font-display text-[11px] tracking-[0.35em] text-primary/70 pr-2">
              {MARQUEE_ITEMS.repeat(3)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
